/** Glossary term definitions for Terminal Vocabulary demo. */

export type VocabularyTerm =
  | 'terminal'
  | 'shell'
  | 'terminal-emulator'
  | 'pty'
  | 'bash'
  | 'zsh'
  | 'console'
  | 'cli';

export interface VocabularyTermDefinition {
  name: string;
  shortName?: string;
  category: 'hardware' | 'software' | 'shell' | 'interface';
  definition: string;
  examples: string[];
  alsoKnownAs?: string[];
}

export const VOCABULARY_TERMS: Record<
  VocabularyTerm,
  VocabularyTermDefinition
> = {
  terminal: {
    name: 'Terminal',
    category: 'hardware',
    definition:
      'Originally a physical device with a screen and keyboard that connected to a mainframe computer. Today, the word usually refers to a terminal emulator.',
    examples: ['VT100', 'VT220', 'IBM 3270'],
    alsoKnownAs: ['TTY', 'Teletype'],
  },
  'terminal-emulator': {
    name: 'Terminal Emulator',
    category: 'software',
    definition:
      'A program that emulates a physical terminal. It draws the character grid, handles input/output, and connects to a shell. This is what you actually run on your computer.',
    examples: [
      'iTerm2',
      'Terminal.app',
      'Windows Terminal',
      'Alacritty',
      'kitty',
      'Warp',
      'Ghostty',
      'WezTerm',
    ],
  },
  shell: {
    name: 'Shell',
    category: 'software',
    definition:
      'A program that interprets commands. The shell reads what you type, executes programs, and handles things like pipes, redirects, and scripting. The terminal emulator is just the window; the shell is the program running inside it.',
    examples: [
      'sh',
      'bash',
      'zsh',
      'fish',
      'ksh',
      'tcsh',
      'PowerShell',
      'nushell',
    ],
  },
  pty: {
    name: 'PTY (Pseudo-Terminal)',
    shortName: 'PTY',
    category: 'software',
    definition:
      'A kernel feature that creates a fake terminal device. It has two ends: the master (connected to your terminal emulator) and the slave (connected to the shell). This is the pipe that connects your terminal to the shell.',
    examples: ['/dev/pts/0', '/dev/ttys000'],
    alsoKnownAs: ['Pseudoterminal', 'Pseudo-TTY'],
  },
  bash: {
    name: 'Bash',
    category: 'shell',
    definition:
      'The "Bourne Again Shell"—the default shell on most Linux systems. Known for its scripting capabilities and POSIX compliance. Uses .bashrc and .bash_profile for configuration.',
    examples: ['#!/bin/bash', 'source ~/.bashrc'],
  },
  zsh: {
    name: 'Zsh',
    category: 'shell',
    definition:
      'The "Z Shell"—the default shell on macOS since Catalina. Has better tab completion, theming (Oh My Zsh), and interactive features than bash. Mostly compatible with bash syntax.',
    examples: ['#!/bin/zsh', 'source ~/.zshrc'],
  },
  console: {
    name: 'Console',
    category: 'interface',
    definition:
      'Historically, the physical terminal directly attached to a computer. In modern usage, often used interchangeably with "terminal" or to mean a text-based interface.',
    examples: [
      'Linux virtual console (Ctrl+Alt+F1)',
      'Browser developer console',
    ],
    alsoKnownAs: ['System console'],
  },
  cli: {
    name: 'CLI (Command-Line Interface)',
    shortName: 'CLI',
    category: 'interface',
    definition:
      'A text-based interface where you interact by typing commands. The opposite of a GUI. Both the shell and programs you run in the terminal are CLIs.',
    examples: ['git', 'npm', 'docker', 'curl'],
  },
};

export const VOCABULARY_CATEGORY_COLORS: Record<string, string> = {
  hardware: 'text-terminal-yellow',
  software: 'text-terminal-cyan',
  shell: 'text-terminal-green',
  interface: 'text-terminal-magenta',
};

export const VOCABULARY_CATEGORY_LABELS: Record<string, string> = {
  hardware: 'Hardware',
  software: 'Software',
  shell: 'Shell',
  interface: 'Interface Type',
};
